import React, { useState, useEffect, useMemo } from 'react';
import {
  BiosProfileStatusWarningModal,
  BiosProactiveWarningModal,
  IconButton,
  Layout,
  LayoutItem,
  ContextMenu,
  ContextMenuItem,
  Suspense,
  useApi,
  Well,
} from '@axiom/ui';
import type { Candidate, User } from '@axiom/validation';
import qs from 'qs';

import { isSalesUser, isTalentSuccessUser } from '../../utils/roles-util';
import { EnvUtil } from '../../utils/env-util';
import { AxiomForTalentUtil } from '../../utils/axiom-for-talent-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { CandidateAttributes } from '../CandidateAttributes/CandidateAttributes';
import { CandidateApi } from '../../api/candidate-api';
import { LegacyUserApi } from '../../api/legacy-user-api';
import { UserAlertsApi } from '../../api/user-alerts-api';
import { TalentProfileEditForm } from '../TalentProfileEditForm/TalentProfileEditForm';
import { useRefreshCandidate } from '../../hooks/useRefreshCandidate';
import TalentResume from '../TalentResume/TalentResume';
import { TalentOpenInAlt } from '../TalentOpenInAlt/TalentOpenInAlt';
import { AvailabilityAlertModal } from '../AvailabilityAlertModal/AvailabilityAlertModal';
import { DeleteAvailabilityAlertModal } from '../DeleteAvailabilityAlertModal/DeleteAvailabilityAlertModal';

import {
  ReasonForNoEdit,
  type ReasonForNoEditType,
} from './talent-profile-status.const';

type EditStatus = {
  canEdit: boolean;
  reason: ReasonForNoEditType | null;
};

export type TalentProfileHeaderProps = {
  candidate: Candidate;
  user: User;
  nextAvailableStatuses?: Array<{
    label: string;
    value: string;
  }>;
  isDetail?: boolean;
  onClickPopOut?: () => void;
  searchTerm?: string;
  hideTitleBar?: boolean;
  onAvailabilityClick?: (candidateId: Candidate['id']) => void;
};

export const TalentProfileHeader = ({
  isDetail = false,
  onClickPopOut,
  searchTerm,
  candidate,
  user,
  nextAvailableStatuses,
  hideTitleBar = false,
  onAvailabilityClick,
}: TalentProfileHeaderProps) => {
  const [{ data: users }] = useApi(LegacyUserApi.readUsers());
  const [
    {
      data: { customAlerts },
    },
  ] = useApi(UserAlertsApi.readUserAlerts());
  const { refreshCandidate } = useRefreshCandidate(candidate.id);

  const [showTalentProfileEditForm, setShowTalentProfileEditForm] =
    useState(false);
  const [isResumeShown, setIsResumeShown] = useState(false);
  const [isBiosProactiveWarningModalOpen, setIsBiosProactiveWarningModalOpen] =
    useState(false);
  const [
    isBiosProfileStatusWarningModalOpen,
    setIsBiosProfileStatusWarningModalOpen,
  ] = useState(false);
  const [showCreateAlertModal, setShowCreateAlertModal] = useState(false);
  const [showEditAlertModal, setShowEditAlertModal] = useState(false);
  const [showDeleteAlertModal, setShowDeleteAlertModal] = useState(false);

  const usersById = useMemo(
    () =>
      users.reduce<Record<User['id'], string>>((map, u) => {
        if (u.id && u.fullName) {
          map[u.id] = u.fullName;
        }
        return map;
      }, {}),
    [users]
  );

  const candidateAlerts = useMemo(
    () => customAlerts.talentAlerts.filter(a => a.candidateId === candidate.id),
    [customAlerts, candidate.id]
  );

  const hasAlerts = candidateAlerts.length > 0;

  const [editStatus, setEditStatus] = useState<EditStatus>({
    canEdit: false,
    reason: ReasonForNoEdit.CanNotChange,
  });

  useEffect(() => {
    const hasPermissions = isTalentSuccessUser(user);
    const canEdit = hasPermissions && (nextAvailableStatuses?.length ?? 0) > 0;

    let reason: ReasonForNoEditType | null = null;
    if (!canEdit) {
      reason = hasPermissions
        ? ReasonForNoEdit.CanNotChange
        : ReasonForNoEdit.NoPermission;
    }

    setEditStatus({
      canEdit,
      reason,
    });
  }, [user, nextAvailableStatuses]);

  const handleFlagToggle = async () => {
    await CandidateApi.updateCandidate(candidate.id, {
      isFlagged: !candidate.isFlagged,
    });
    // Refresh single candidate (sidebar) plus Talent + My Bench grids so the
    // flag change is reflected in the grid without a page refresh (BACK-7469).
    await refreshCandidate(candidate.id);
  };

  const handleOpenFullPageView = () => {
    const url =
      searchTerm && searchTerm.length >= 2
        ? `/talent-detail/${candidate.id}?search=${searchTerm}`
        : `/talent-detail/${candidate.id}`;
    window.open(url, '_blank');
  };

  const handleFindSimilarTalent = () => {
    const url = `${EnvUtil.tableauSimilarTalentUrl}${qs.stringify({ TNP: candidate.calculatedDisplayName }, { addQueryPrefix: true })}`;
    window.open(url, '_blank');
  };

  const closeBiosProactiveWarningModal = () => {
    setIsBiosProactiveWarningModalOpen(false);
  };

  const closeBiosProfileStatusWarningModal = () => {
    setIsBiosProfileStatusWarningModalOpen(false);
  };

  const handleOpenBio = () => {
    switch (
      CandidateUtil.getProactiveBadgeStatus(
        candidate.profileStatus,
        candidate.isProfileShared
      )
    ) {
      case 'open': {
        AxiomForTalentUtil.openBio(candidate.id);
        break;
      }
      case 'closed': {
        setIsBiosProactiveWarningModalOpen(true);
        break;
      }
      default: {
        setIsBiosProfileStatusWarningModalOpen(true);
      }
    }
  };

  const handleOpenAnonymousBio = () => {
    AxiomForTalentUtil.openAnonymousBio(candidate.id);
  };

  const toggleResume = () => {
    setIsResumeShown(!isResumeShown);
  };

  const closeResume = () => {
    setIsResumeShown(false);
  };

  return (
    <>
      {isBiosProfileStatusWarningModalOpen ? (
        <BiosProfileStatusWarningModal
          profileStatus={candidate.profileStatus}
          onConfirm={() => {
            AxiomForTalentUtil.openBio(candidate.id);
            closeBiosProfileStatusWarningModal();
          }}
          onClose={closeBiosProfileStatusWarningModal}
        />
      ) : null}
      {isBiosProactiveWarningModalOpen ? (
        <BiosProactiveWarningModal
          onConfirm={() => {
            AxiomForTalentUtil.openBio(candidate.id);
            closeBiosProactiveWarningModal();
          }}
          onClose={closeBiosProactiveWarningModal}
        />
      ) : null}
      {isResumeShown ? (
        <TalentResume onCancel={closeResume} talent={candidate} />
      ) : null}
      {showCreateAlertModal ? (
        <AvailabilityAlertModal
          onClose={() => {
            setShowCreateAlertModal(false);
          }}
          candidate={candidate}
        />
      ) : null}
      {showEditAlertModal ? (
        <AvailabilityAlertModal
          onClose={() => {
            setShowEditAlertModal(false);
          }}
          candidate={candidate}
          existingAlerts={candidateAlerts}
        />
      ) : null}
      {showDeleteAlertModal ? (
        <DeleteAvailabilityAlertModal
          onClose={() => {
            setShowDeleteAlertModal(false);
          }}
          candidateId={candidate.id}
        />
      ) : null}
      <div data-test="talentTabHeader">
        {!hideTitleBar && (
          <Well background="blue">
            <Layout position="middle">
              <LayoutItem fluid>
                <CandidateAttributes
                  name="CANDIDATE_ATTRIBUTES"
                  candidate={candidate}
                  usersById={usersById}
                  showAlertIcon={hasAlerts}
                  inlineBadges={isDetail}
                >
                  <ContextMenu
                    direction="left"
                    anchor={
                      <IconButton
                        icon="vertical-ellipsis"
                        variation="minimal"
                        pattern="secondary"
                        inverse
                        name="CANDIDATE_CONTEXT_MENU"
                      />
                    }
                  >
                    {!!onClickPopOut && !isDetail && (
                      <ContextMenuItem
                        onClick={handleOpenFullPageView}
                        name="CANDIDATE_CONTEXT_MENU_FULL_PAGE"
                      >
                        Open in full page view
                      </ContextMenuItem>
                    )}
                    <ContextMenuItem name="OPEN_BIO" onClick={handleOpenBio}>
                      Open bio
                    </ContextMenuItem>
                    <ContextMenuItem
                      name="OPEN_ANONYMOUS_BIO"
                      onClick={handleOpenAnonymousBio}
                    >
                      Open anonymous bio
                    </ContextMenuItem>
                    <ContextMenuItem name="OPEN_RESUME" onClick={toggleResume}>
                      Open resume
                    </ContextMenuItem>
                    {AxiomForTalentUtil.canViewAft(candidate, user) && (
                      <ContextMenuItem
                        name="CANDIDATE_CONTEXT_MENU_OPEN_AFT"
                        onClick={() => {
                          AxiomForTalentUtil.openAftProfile(candidate.id);
                        }}
                      >
                        Open in Axiom for Talent
                      </ContextMenuItem>
                    )}
                    <TalentOpenInAlt candidate={candidate} />
                    {editStatus.canEdit ? (
                      <ContextMenuItem
                        onClick={() => {
                          setShowTalentProfileEditForm(true);
                        }}
                        name="CANDIDATE_CONTEXT_MENU_EDIT_PROFILE_STATUS"
                      >
                        Edit profile status
                      </ContextMenuItem>
                    ) : null}
                    <ContextMenuItem
                      onClick={() => onAvailabilityClick?.(candidate.id)}
                      name="CANDIDATE_CONTEXT_MENU_AVAILABILITY"
                    >
                      Show availability calculation
                    </ContextMenuItem>
                    <ContextMenuItem
                      onClick={handleFindSimilarTalent}
                      name="CANDIDATE_CONTEXT_MENU_SIMILAR_TALENT"
                    >
                      Find similar talent in Tableau
                    </ContextMenuItem>
                    <ContextMenuItem
                      onClick={handleFlagToggle}
                      disabled={isSalesUser(user)}
                      name="CANDIDATE_CONTEXT_MENU_FLAG"
                    >
                      {candidate.isFlagged ? 'Remove flag' : 'Flag profile'}
                    </ContextMenuItem>
                    {hasAlerts ? (
                      <>
                        <ContextMenuItem
                          onClick={() => {
                            setShowEditAlertModal(true);
                          }}
                          name="CANDIDATE_CONTEXT_MENU_EDIT_ALERT"
                        >
                          Edit availability alert
                        </ContextMenuItem>
                        <ContextMenuItem
                          onClick={() => {
                            setShowDeleteAlertModal(true);
                          }}
                          name="CANDIDATE_CONTEXT_MENU_DELETE_ALERT"
                        >
                          Delete availability alert
                        </ContextMenuItem>
                      </>
                    ) : (
                      <ContextMenuItem
                        onClick={() => {
                          setShowCreateAlertModal(true);
                        }}
                        name="CANDIDATE_CONTEXT_MENU_CREATE_ALERT"
                      >
                        Create availability alert
                      </ContextMenuItem>
                    )}
                  </ContextMenu>
                </CandidateAttributes>
              </LayoutItem>
            </Layout>
          </Well>
        )}
        {showTalentProfileEditForm ? (
          <Suspense>
            <TalentProfileEditForm
              name="TALENT_PROFILE_EDIT_FORM_FROM_HEADER"
              candidate={candidate}
              onClose={() => {
                setShowTalentProfileEditForm(false);
              }}
            />
          </Suspense>
        ) : null}
      </div>
    </>
  );
};
